// useVideoCommentThread.ts — orchestration hook for the video watch page's comment
// thread. Composes useVideoComments(videoId) + useSession() — never imports
// stores directly. A trimmed version of post-detail's useCommentThread: video
// comments have no sort, edit, or delete UI (owner scope), so this only owns
// expand state and the single-open inline reply (+ draft-discard guard).

import { useCallback, useState } from 'react';
import { useSession, useVideoComments } from '@/hooks';
import type { VideoComment } from '@/core/models/videoComment';

export interface VideoCommentThread {
  liveCount: number;
  topLevel: VideoComment[];
  repliesFor: (topId: string) => VideoComment[];
  isExpanded: (topId: string) => boolean;
  toggleExpanded: (topId: string) => void;
  // reply (single-open)
  openReplyId: string | null;
  replyTargetHandle: string | null;
  openReply: (target: VideoComment, targetHandle?: string | null) => void;
  cancelReply: () => void;
  setReplyDraft: (d: string) => void;
  submitReply: (body: string) => void;
  // like
  toggleLike: (id: string) => void;
  // top-level composer
  addTopLevel: (body: string) => void;
}

export function useVideoCommentThread(videoId: string): VideoCommentThread {
  const { comments, topLevel, repliesFor: repliesForRaw, addComment, addReply, toggleLike } =
    useVideoComments(videoId);
  const { currentUser } = useSession();

  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());

  const [openReplyId, setOpenReplyId] = useState<string | null>(null);
  const [replyParentId, setReplyParentId] = useState<string | null>(null);
  const [replyTargetHandle, setReplyTargetHandle] = useState<string | null>(null);
  const [replyHadHandle, setReplyHadHandle] = useState<boolean>(false);
  const [replyDraft, setReplyDraft] = useState<string>('');

  const liveCount = comments.filter((c) => !c.deleted).length;

  const repliesFor = useCallback((topId: string) => repliesForRaw(topId), [repliesForRaw]);

  const isExpanded = useCallback((id: string) => expandedSet.has(id), [expandedSet]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const cancelReply = useCallback(() => {
    setOpenReplyId(null);
    setReplyParentId(null);
    setReplyTargetHandle(null);
    setReplyHadHandle(false);
    setReplyDraft('');
  }, []);

  const openReply = useCallback(
    (target: VideoComment, targetHandle?: string | null) => {
      // Draft-discard guard: switching to a different comment with a non-empty draft confirms first.
      if (openReplyId !== null && openReplyId !== target.id && replyDraft.trim() !== '') {
        if (!window.confirm('Discard your draft?')) return;
      }

      const isTopLevel = target.parentId === null;
      // Flatten rule: parentId is always the top-level ancestor.
      const parentId = isTopLevel ? target.id : target.parentId!;
      const hadHandle = !isTopLevel;
      const resolvedHandle = hadHandle ? (targetHandle ?? null) : null;

      setOpenReplyId(target.id);
      setReplyParentId(parentId);
      setReplyHadHandle(hadHandle);
      setReplyTargetHandle(resolvedHandle);
      setReplyDraft('');
    },
    [openReplyId, replyDraft],
  );

  const submitReply = useCallback(
    (body: string) => {
      // No invented "guest" author — a signed-out reply is a no-op. The Reply
      // button itself is already auth-gated (VideoCommentCard's useRequireAuth),
      // so this only guards against reaching here some other way.
      if (!currentUser || !openReplyId || !replyParentId) return;

      addReply({
        videoId,
        authorId: currentUser.id,
        parentId: replyParentId,
        body,
        replyingToHandle: replyHadHandle ? replyTargetHandle : null,
      });

      setExpandedSet((prev) => {
        const next = new Set(prev);
        next.add(replyParentId);
        return next;
      });

      cancelReply();
    },
    [currentUser, openReplyId, replyParentId, replyHadHandle, replyTargetHandle, addReply, videoId, cancelReply],
  );

  const addTopLevel = useCallback(
    (body: string) => {
      // No invented "guest" author — VideoCommentComposer already renders a
      // SignInCta instead of the input when signed out, so this only guards
      // against reaching here some other way.
      if (!currentUser) return;
      addComment({ videoId, authorId: currentUser.id, body });
    },
    [addComment, videoId, currentUser],
  );

  return {
    liveCount,
    topLevel,
    repliesFor,
    isExpanded,
    toggleExpanded,
    openReplyId,
    replyTargetHandle,
    openReply,
    cancelReply,
    setReplyDraft,
    submitReply,
    toggleLike,
    addTopLevel,
  };
}
